import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Cpu, Code2, Brain, BarChart3, Star, Lightbulb, Users, Globe2 } from "lucide-react";
import { Images } from "@assets/index";

export default function Hero() {
  const { t } = useTranslation("landing");

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="home" className="relative overflow-hidden bg-cinematic pt-28 lg:pt-32 w-full">
      {/* Animated glow orbs */}
      <div className="pointer-events-none absolute -top-32 left-1/3 h-[500px] w-[500px] rounded-full bg-[oklch(0.62_0.27_0/0.35)] blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-32 right-0 h-[420px] w-[420px] rounded-full bg-[oklch(0.55_0.24_295/0.4)] blur-[120px]" />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 pb-20 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28">
        {/* LEFT */}
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 rounded-full glass-card px-3.5 py-1.5 text-xs font-semibold text-white"
          >
            {t("hero.badge")}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-6 text-[40px] font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-[64px]"
          >
            {t("hero.titleWhite1")}{" "}
            <span className="gradient-text-warm text-glow block sm:inline">{t("hero.titleWhite2")} {t("hero.titleGradient")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-white/75"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={() => scrollToSection("apply")}
              className="inline-flex items-center gap-2 rounded-full btn-cinematic px-6 py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 cursor-pointer border-0"
            >
              {t("hero.applyCTA")} <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => scrollToSection("program")}
              className="inline-flex items-center gap-2 rounded-full glass-card px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/15 cursor-pointer border-0 bg-transparent"
            >
              {t("hero.detailsCTA")}
            </button>
          </motion.div>

          <div className="mt-7 flex items-center gap-3 text-sm text-white/70">
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="font-medium">{t("hero.trust")}</span>
          </div>

          {/* Partnership logos */}
          <div className="mt-10 max-w-xl">
            <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
              <span className="h-px flex-1 bg-white/15" />
              In Partnership With
              <span className="h-px flex-1 bg-white/15" />
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-8 sm:gap-12">
              <img src={Images.iag} alt="Innovation Acceleration Group" className="h-8 w-auto object-contain opacity-90 sm:h-20" />
            </div>
          </div>

        </div>

        {/* RIGHT */}
        <div className="relative lg:col-span-5 flex items-center justify-center">
          <div className="absolute inset-0 -z-10 dot-grid opacity-30" />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative aspect-square w-full max-w-[420px] overflow-hidden rounded-[28px] ring-1 ring-white/20 shadow-[0_30px_80px_-20px_oklch(0.62_0.27_0/0.5)]"
          >
            <img
              src={Images.heroStudents}
              alt="Students collaborating on an AI project"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[oklch(0.55_0.24_295/0.35)] via-transparent to-[oklch(0.72_0.22_35/0.25)] mix-blend-overlay" />
          </motion.div>

          {/* Floating cards */}
          <FloatChip icon={<Cpu className="h-5 w-5" />} label="AI Chip" className="-left-4 top-10 float-slow" />
          <FloatChip icon={<Code2 className="h-5 w-5" />} label="Code" className="-right-3 top-24 float-slower" />
          <FloatChip icon={<Brain className="h-5 w-5" />} label="ML" className="-left-2 bottom-24 float-slower" />
          <FloatChip icon={<BarChart3 className="h-5 w-5" />} label="Insights" className="-right-4 bottom-10 float-slow" />
        </div>
      </div>

      {/* Bottom pills */}
      <div className="relative border-t border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 px-4 sm:px-6 py-6 sm:grid-cols-4 lg:px-8">
          <Pill icon={<Lightbulb className="h-4 w-4" />}>{t("hero.card1")}</Pill>
          <Pill icon={<Users className="h-4 w-4" />}>{t("hero.card2")}</Pill>
          <Pill icon={<Code2 className="h-4 w-4" />}>{t("hero.card3")}</Pill>
          <Pill icon={<Globe2 className="h-4 w-4" />}>{t("hero.card4")}</Pill>
        </div>
      </div>
    </section>
  );
}

function FloatChip({ icon, label, className = "" }: { icon: React.ReactNode; label: string; className?: string }) {
  return (
    <div
      className={`absolute hidden items-center gap-2 rounded-2xl glass-card px-3.5 py-2.5 text-xs font-semibold text-white md:inline-flex ${className}`}
    >
      <span className="grid h-7 w-7 place-items-center rounded-lg bg-white/15 text-white">{icon}</span>
      {label}
    </div>
  );
}

function Pill({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2.5 rounded-full glass-card px-4 py-2.5 text-sm font-semibold text-white">
      <span className="text-[oklch(0.85_0.15_45)]">{icon}</span>
      {children}
    </div>
  );
}
